import {LoadingButton} from '@mui/lab';
import {Box, Checkbox, FormControlLabel, Grid} from '@mui/material';
import dayjs from 'dayjs';
import {useFormik} from 'formik';
import {enqueueSnackbar} from 'notistack';
import {ChangeEvent, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {ExportField} from '../../../interfaces/Export';
import {saveFile} from '../../../utils/file';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';
import SnackbarMessages from '../../common/SnackbarMessages';
import {WarningHazardsData} from './WarningHazardReport';

enum ColumnsCount {
  ONE = 1,
  TWO = 2,
  THREE = 3,
  FOUR = 4,
  SIX = 6,
  TWELVE = 12,
}

interface Props {
  value?: WarningHazardsData;
  columns?: ColumnsCount;
}

const xsVsColumns: {
  [value in ColumnsCount]: number;
} = {
  [ColumnsCount.ONE]: 12,
  [ColumnsCount.TWO]: 6,
  [ColumnsCount.THREE]: 4,
  [ColumnsCount.FOUR]: 3,
  [ColumnsCount.SIX]: 2,
  [ColumnsCount.TWELVE]: 1,
};

const exportFieldOptions: ExportField[] = [
  {field: 'id', label: 'ID'},
  {field: 'no_heartbeat', label: 'Heartbeat'},
  {field: 'no_location', label: 'No Location'},
  {field: 'camera', label: 'Camera'},
  {field: 'speed_limit', label: 'Speed Limit'},
  {field: 'asset_name', label: 'Machine Name'},
  {field: 'machine_type', label: 'Machine Type'},
  {field: 'site_name', label: 'Site'},
  {field: 'name', label: 'Camera Name'},
  {field: 'timestamp', label: 'Timestamp'},
  {field: 'speed', label: 'Last Speed'},
  {field: 'max_speed', label: 'Max Speed'},
  {field: 'hazard_person', label: 'People Hazard'},
  {field: 'warning_person', label: 'People Warning', hidden: true},
  {field: 'hazard_vehicle', label: 'Machine Hazard'},
  {field: 'warning_vehicle', label: 'Machine Warning', hidden: true},
  {field: 'latitude', label: 'Lat'},
  {field: 'longitude', label: 'Lon'},
];

const WarningHazardExportFieldForm = ({value, columns = 3}: Props) => {
  const [isFetching, setIsFetching] = useState(false);
  const [fields, setFields] = useState<string[]>(
    exportFieldOptions
      .filter((field) => !field.hidden)
      .map((field) => field.field)
  );

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    field: string
  ) => {
    if (checked) {
      fields.push(field);
      setFields([...fields]);
    } else {
      const index = fields.indexOf(field);
      fields.splice(index, 1);
      setFields([...fields]);
    }
  };

  const formik = useFormik<string[]>({
    initialValues: fields,
    onSubmit: async () => {
      await fetchData(fields);
    },
  });

  const fetchData = async (selectedFields: string[]) => {
    setIsFetching(true);
    try {
      const params = {
        status: value?.params.status,
        site_id: value?.params.site_id,
        date_start: value?.params.date_start,
        date_end: value?.params.date_end,
        export: 'excel',
        fields: selectedFields,
      };
      const resp = await API.get(`${apiBaseUrl}/safeye-nano-node`, {
        params,
        responseType: 'blob',
      });
      saveFile(
        resp.data,
        `hazard_ai_warnings_${value?.params.date_start ?? dayjs().format('YYYY-MM-DD')}_${
          value?.params.date_end ?? dayjs().format('YYYY-MM-DD')
        }.xlsx`
      );
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_9a3f2c14');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setIsFetching(false);
  };

  return (
    <Box
      component="form"
      display="flex"
      flexDirection="column"
      position="relative"
      onSubmit={formik.handleSubmit}
    >
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" alignItems="center" width="100%" gap={1.5}>
          <Box fontSize={24}>Select Fields to Export</Box>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" gap={3}>
        <Grid container sx={{p: 1, height: 300, overflowY: 'auto'}}>
          {exportFieldOptions.map((item: ExportField) => (
            <Grid item xs={xsVsColumns[columns]} key={item.field}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={fields.indexOf(item.field) !== -1}
                    onChange={(event, checked) => {
                      handleChange(event, checked, item.field);
                    }}
                  />
                }
                label={item.label}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box display="flex" justifyContent="end" mt="20px">
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          loading={isFetching}
          type="submit"
        >
          Export to Excel
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default WarningHazardExportFieldForm;
